import React, { useEffect, useState } from "react";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import { getNotice, updateNotice } from "../../store/slices/notice";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { uploadImage } from "../../utils/helpers/fileupload";
import { MdDelete } from "react-icons/md";
import {
  FiHome,
  FiChevronRight,
} from "react-icons/fi";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";

const requiredMessages = {
  usertype: "Please select user type",
  date: "Please select notice date",
  description: "Please enter description",
};

export default function EditNotice() {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { loading } = useSelector((state) => state.notice || {});

  const [errors, setErrors] = useState({});
  const [newFiles, setNewFiles] = useState([]);

  const [formData, setFormData] = useState({
    usertype: "",
    date: "",
    description: "",
    status: 1,
    files: [],
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],

    content: "",

    onUpdate: ({ editor }) => {
      const html = editor.getHTML();

      setFormData((prev) => ({
        ...prev,
        description: html,
      }));

      setErrors((prev) => ({
        ...prev,
        description: "",
      }));
    },
  });
  /* ---------------- Fetch Notice ---------------- */
  useEffect(() => {
    if (!id) return;

    dispatch(getNotice({ id }))
      .unwrap()
      .then((res) => {
        const notice = res?.data?.[0];
        if (notice) {
          setFormData({
            usertype: String(notice.usertype),
            date: notice.date ? notice.date.split("T")[0] : "",
            description: notice.description || "",
            status: notice.status ?? 1,
            files: notice.files || [],
          });

          // sync the Tiptap editor with the fetched description
          if (editor && notice.description) {
            editor.commands.setContent(notice.description);
          }
        }
      })
      .catch(() => toast.error("Failed to fetch notice"));
  }, [id, dispatch, editor]);
  /* ---------------- Handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    if (formData.files.length + selected.length > 5) {
      setErrors((p) => ({ ...p, files: "Maximum 5 files allowed" }));
      return;
    }
    setNewFiles(selected);
    setErrors((p) => ({ ...p, files: "" }));
  };

  const removeExistingFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  /* ---------------- Submit ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};
    Object.keys(requiredMessages).forEach((key) => {
      if (!formData[key]) newErrors[key] = requiredMessages[key];
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    try {
      let fileUrls = [...formData.files];

      for (const file of newFiles) {
        const url = await uploadImage(file, token);
        if (url) fileUrls.push(url);
      }

      const payload = {
        id,
        usertype: Number(formData.usertype),
        date: formData.date,
        description: formData.description,
        files: fileUrls,
        status: formData.status,
      };

      await dispatch(updateNotice(payload)).unwrap();
      toast.success("Notice updated successfully");
      navigate("/institute/notices");
    } catch (err) {
      toast.error(err?.message || "Failed to update notice");
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );
  }


  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/institute/home"><FiHome style={{ color: "white", marginTop: "-4px" }} /></Link>
            </div>

            <FiChevronRight className="separator" />

            <Link to="/institute/notices">Notice</Link>

            <FiChevronRight className="separator" />

            <span className="active">Edit Notice</span>
          </div>

        </div>
      </div>
      <div className="acs-card">
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">

            {/* User Type */}
            <Col lg={4} md={6}>
              <Form.Group>
                <Form.Label>User Type</Form.Label>
                <Form.Select
                  name="usertype"
                  value={formData.usertype}
                  onChange={handleChange}
                >
                  <option value="">Select User Type</option>
                  <option value="1">Student</option>
                  <option value="2">Staff</option>
                  <option value="3">All</option>
                </Form.Select>
                {errors.usertype && (
                  <small className="text-danger">{errors.usertype}</small>
                )}
              </Form.Group>
            </Col>

            {/* Date */}
            <Col lg={4} md={6}>
              <Form.Group>
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={formData.date}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={handleChange}
                />
                {errors.date && (
                  <small className="text-danger">{errors.date}</small>
                )}
              </Form.Group>
            </Col>

            {/* Description */}
            <Col lg={10}>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <div className="border rounded">
                  <div
                    className="d-flex flex-wrap gap-2 p-2 border-bottom"
                    style={{ background: "#f8f9fa" }}
                  >
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => editor?.chain().focus().toggleBold().run()}
                    >
                      <b>B</b>
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => editor?.chain().focus().toggleItalic().run()}
                    >
                      <i>I</i>
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => editor?.chain().focus().toggleUnderline().run()}
                    >
                      <u>U</u>
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().toggleBulletList().run()
                      }
                    >
                      • List
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().toggleOrderedList().run()
                      }
                    >
                      1. List
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().setTextAlign("left").run()
                      }
                    >
                      Left
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().setTextAlign("center").run()
                      }
                    >
                      Center
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().setTextAlign("right").run()
                      }
                    >
                      Right
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().undo().run()
                      }
                    >
                      Undo
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() =>
                        editor?.chain().focus().redo().run()
                      }
                    >
                      Redo
                    </button>
                  </div>
                  <EditorContent
                    editor={editor}
                    className="notice-editor"
                  />
                </div>
                {errors.description && (
                  <small className="text-danger">
                    {errors.description}
                  </small>
                )}
              </Form.Group>
            </Col>


            {/* Upload New Files */}
            <Col md={6}>
              <Form.Group>
                <Form.Label>Add Attachments (Max 5)</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={handleFileChange}
                />
                {errors.files && (
                  <small className="text-danger">{errors.files}</small>
                )}
              </Form.Group>
            </Col>

            {/* Existing Files */}
            {formData.files.length > 0 && (
              <Col md={12}>

                <Form.Label>Existing Attachments</Form.Label>

                <ul className="d-flex flex-row , align-items-center" style={{ paddingLeft: "18px", listStyleType: "none" }}>

                  {formData.files.map((file, i) => (

                    <li className="mx-2 p-2 d-flex  justify-content-center align-items-center " key={i} style={{ fontSize: "small", backgroundColor: "whitesmoke", borderRadius: "10px" }}>

                      <a href={file} target="_blank" rel="noreferrer">
                        View file {i + 1}
                      </a>

                      <button
                        type="button"
                        className="ms-2 btn btn-link text-danger p-0"
                        onClick={() => removeExistingFile(i)}
                      >
                        <MdDelete />
                      </button>
                    </li>
                  ))}
                </ul>
              </Col>
            )}




          </Row>

          {/* Actions */}
          <div className="an-actions mt-5">
            <button
              type="button"
              className="btn-cancel"
              style={{ color: "black" }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button className="btn-submit" type="submit">
              Update
            </button>
          </div>
        </Form>
      </div>
    </>
  );
}
